import { Schema } from 'effect';
import { ChecklistSchema } from './checklist';
import { VehicleSchema } from './vehicle';

export const CenterOpenState = {
  CENTER_OPENING_CHECKLIST: 'center_opening_checklist',
  CENTER_OPEN: 'center_open',
  CENTER_OPEN_SERVICE_CLOSED: 'center_open_service_closed',
  CENTER_OPEN_CAN_BE_CLOSED: 'center_open_can_be_closed',
  CENTER_CLOSING_CHECKLIST: 'center_closing_checklist',
  CENTER_CLOSED: 'center_closed'
} as const;

export const CenterOpenStateSchema = Schema.Literal(
  CenterOpenState.CENTER_OPENING_CHECKLIST,
  CenterOpenState.CENTER_OPEN,
  CenterOpenState.CENTER_OPEN_SERVICE_CLOSED,
  CenterOpenState.CENTER_OPEN_CAN_BE_CLOSED,
  CenterOpenState.CENTER_CLOSING_CHECKLIST,
  CenterOpenState.CENTER_CLOSED
);

export type CenterOpenState = Schema.Schema.Type<typeof CenterOpenStateSchema>;

export const CenterStateTransitionSchema = Schema.transform(
  Schema.Struct({
    allowed_to_complete_name: Schema.optional(Schema.NullOr(Schema.String)),
    can_you_complete: Schema.Boolean
  }),
  Schema.Struct({
    allowedToCompleteName: Schema.optional(Schema.NullOr(Schema.String)),
    canYouComplete: Schema.Boolean
  }),
  {
    strict: true,
    decode: (api) => ({
      allowedToCompleteName: api.allowed_to_complete_name,
      canYouComplete: api.can_you_complete
    }),
    encode: (client) => ({
      allowed_to_complete_name: client.allowedToCompleteName,
      can_you_complete: client.canYouComplete
    })
  }
);
export type CenterStateTransition = Schema.Schema.Type<typeof CenterStateTransitionSchema>;

export const CenterEmailSchema = Schema.transform(
  Schema.Struct({
    id: Schema.optional(Schema.Number),
    email_address: Schema.String
  }),
  Schema.Struct({
    id: Schema.optional(Schema.Number),
    emailAddress: Schema.String
  }),
  {
    strict: true,
    decode: (api) => ({
      id: api.id,
      emailAddress: api.email_address
    }),
    encode: (client) => ({
      id: client.id,
      email_address: client.emailAddress
    })
  }
);
export type CenterEmail = Schema.Schema.Type<typeof CenterEmailSchema>;

export const CenterSchema = Schema.transform(
  Schema.Struct({
    id: Schema.optional(Schema.NullOr(Schema.Number)),
    name: Schema.String,
    vehicles: Schema.optional(Schema.NullOr(Schema.Array(Schema.encodedSchema(VehicleSchema)))),
    open_state: Schema.optional(Schema.NullOr(CenterOpenStateSchema)),
    timezone: Schema.String,
    email_addresses: Schema.optional(Schema.NullOr(Schema.Array(Schema.encodedSchema(CenterEmailSchema)))),
    checklists: Schema.Array(Schema.encodedSchema(ChecklistSchema)),
    is_training_mode_enabled: Schema.optional(Schema.Boolean)
  }),
  Schema.Struct({
    id: Schema.optional(Schema.NullOr(Schema.Number)),
    name: Schema.String,
    vehicles: Schema.optional(Schema.NullOr(Schema.Array(VehicleSchema))),
    openState: Schema.optional(Schema.NullOr(CenterOpenStateSchema)),
    timezone: Schema.String,
    emailAddresses: Schema.optional(Schema.NullOr(Schema.Array(CenterEmailSchema))),
    checklists: Schema.Array(ChecklistSchema),
    isTrainingModeEnabled: Schema.optional(Schema.Boolean)
  }),
  {
    strict: true,
    decode: (api) => ({
      id: api.id,
      name: api.name,
      vehicles: api.vehicles,
      openState: api.open_state,
      timezone: api.timezone,
      emailAddresses: api.email_addresses,
      checklists: api.checklists,
      isTrainingModeEnabled: api.is_training_mode_enabled
    }),
    encode: (client) => ({
      id: client.id,
      name: client.name,
      vehicles: client.vehicles,
      open_state: client.openState,
      timezone: client.timezone,
      email_addresses: client.emailAddresses,
      checklists: client.checklists,
      is_training_mode_enabled: client.isTrainingModeEnabled
    })
  }
);
export type Center = Schema.Schema.Type<typeof CenterSchema>;

export const CenterOpenStateLaunchpadChecklistsSchema = Schema.transform(
  Schema.Struct({
    launchpad_id: Schema.Number,
    launchpad_letter: Schema.String,
    vehicle_id: Schema.Number,
    vehicle_name: Schema.String,
    checklist: Schema.optional(Schema.NullOr(Schema.encodedSchema(ChecklistSchema)))
  }),
  Schema.Struct({
    launchpadId: Schema.Number,
    launchpadLetter: Schema.String,
    vehicleId: Schema.Number,
    vehicleName: Schema.String,
    checklist: Schema.optional(Schema.NullOr(ChecklistSchema))
  }),
  {
    strict: true,
    decode: (api) => ({
      launchpadId: api.launchpad_id,
      launchpadLetter: api.launchpad_letter,
      vehicleId: api.vehicle_id,
      vehicleName: api.vehicle_name,
      checklist: api.checklist
    }),
    encode: (client) => ({
      launchpad_id: client.launchpadId,
      launchpad_letter: client.launchpadLetter,
      vehicle_id: client.vehicleId,
      vehicle_name: client.vehicleName,
      checklist: client.checklist
    })
  }
);
export type CenterOpenStateLaunchpadChecklists = Schema.Schema.Type<typeof CenterOpenStateLaunchpadChecklistsSchema>;

export const CenterOpenStateChecklistsSchema = Schema.Struct({
  center: Schema.optional(Schema.NullOr(ChecklistSchema)),
  launchpads: Schema.Array(CenterOpenStateLaunchpadChecklistsSchema)
});
export type CenterOpenStateChecklists = Schema.Schema.Type<typeof CenterOpenStateChecklistsSchema>;

export const CenterOpenStateNotificationSchema = Schema.transform(
  Schema.Struct({
    open_state: CenterOpenStateSchema,
    transition: Schema.optional(Schema.NullOr(Schema.encodedSchema(CenterStateTransitionSchema)))
  }),
  Schema.Struct({
    openState: CenterOpenStateSchema,
    transition: Schema.optional(Schema.NullOr(CenterStateTransitionSchema))
  }),
  {
    strict: true,
    decode: (api) => ({
      openState: api.open_state,
      transition: api.transition
    }),
    encode: (client) => ({
      open_state: client.openState,
      transition: client.transition
    })
  }
);
export type CenterOpenStateNotification = Schema.Schema.Type<typeof CenterOpenStateNotificationSchema>;

export const SetCenterOpenStateRequestSchema = Schema.transform(
  Schema.Struct({
    open_state: CenterOpenStateSchema,
    checklists: Schema.encodedSchema(CenterOpenStateChecklistsSchema)
  }),
  Schema.Struct({
    openState: CenterOpenStateSchema,
    checklists: CenterOpenStateChecklistsSchema
  }),
  {
    strict: true,
    decode: (api) => ({
      openState: api.open_state,
      checklists: api.checklists
    }),
    encode: (client) => ({
      open_state: client.openState,
      checklists: client.checklists
    })
  }
);
export type SetCenterOpenStateRequest = Schema.Schema.Type<typeof SetCenterOpenStateRequestSchema>;
